import { useEffect, useState } from 'react';

import { Option } from '../../../../types/Option';
import { META_COLLECT_API } from '../../../../api';
import { DontExistsItem } from '../../../../components/DontExistsItem';
import { DontExistsItemFlex } from '../../../../components/DontExistsItem/styles';
import { ListCardsPQFLVisits } from '../../../../components/List/Collects/ListCardsPQFLVisits';
import { LoadingData } from '../../../../components/LoadingData';
import { ViewMode } from '../../../../components/PageHeader';
import { useSigninContext } from '../../../../context/AuthProviderPages/Signin';
import { usePageContext } from '../../../../context/usePage';
import { Paginator } from '../components/paginator';
import { ContentWrapper } from './components/content-wrapper';
import { Table } from './components/table';
import { ModalInfoTemplete } from '../../../../components/Modal/ModalInfoTemplete';
import { CustomButtonWithIcon } from '../../../../components/Buttons/CustomButtonWithIcon';

export type PQFLAnswerItem = {
  id: number;
  valor: string;
  status: string;
  observacao: string;
  grau1_pergunta: string;
  grau2_pergunta: string;
  texto_pergunta: string;
  observacao_pergunta: string | null;
  tipo_pergunta: number;
  obrigatorio_pergunta: string;
  ordem_pergunta: number;
  data_criacao: string;
  data_atualizacao: string;
  possui_foto?: boolean;
  foto_base64?: string;
};

export type PQFLAnswer = {
  id: number;
  status: string;
  data_criacao: string;
  data_atualizacao: string;
  questionario: {
    id: number;
    descricao: string;
  };
  itens: PQFLAnswerItem[];
};

export type PQFLVisitRow = {
  id: number;
  status: string;
  descricao: string | null;
  data_inicio: string;
  data_final: string | null;
  recomendacao_tecnica: string | null;
  assinatura_produtor: string | null;
  assinatura_tecnico: string | null;
  propriedade: {
    id: number;
    ativo: string;
    cnpj_cpf_cei: string;
    nome_propriedade: string;
    nome_produtor: string;
    nome_municipio: string;
    id_sigla_uf: string;
    logradouro: string;
    numero: string;
    data_atualizacao: string;
    matricula: string;
  };
  unidade: {
    id: number;
    nome: string;
  };
  funcionario: {
    id: number;
    nome: string;
  };
  respostas: PQFLAnswer[];
};

type VisitStatus = 'agendada' | 'em_andamento' | 'finalizada' | 'cancelada';

export const PQFLVisitsPage = ({ status }: { status: VisitStatus }) => {
  const { setSelectedPage } = usePageContext();
  const { path } = useSigninContext();

  const [modalMode, setModalMode] = useState<
    'visualize' | 'print' | 'print-with-photos' | null
  >(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [rows, setRows] = useState<PQFLVisitRow[]>([]);
  const [selectedRow, setSelectedRow] = useState<PQFLVisitRow | null>(null);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [count, setCount] = useState<number>(0);
  const [limit, setLimit] = useState<number>(15);
  const [page, setPage] = useState<number>(1);
  const [modifyViewMode, setModifyViewMode] = useState<ViewMode>({
    have: true,
    mode: 'card',
  });
  const [showPrintSelection, setShowPrintSelection] = useState<boolean>(false);

  const fetchVisits = async () => {
    try {
      const url = `/pqfl/visits?limit=${limit}&page=${page}&status=${status}`;
      const { data: response } = await META_COLLECT_API.get(url, {
        headers: { path },
      });
      setHasMore(response.hasMore);
      setCount(response.count);
      setRows(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleVisualize = (visit: PQFLVisitRow) => {
    setSelectedRow(visit);
    setModalMode('visualize');
  };

  const handlePrintRequest = (visit: PQFLVisitRow, withPhotos: boolean) => {
    setSelectedRow(visit);
    setModalMode(withPhotos ? 'print-with-photos' : 'print');
  };

  const statusOptionMap: Record<string, Option['option']> = {
    agendada: 'visitas_agendadas',
    em_andamento: 'visitas_em_andamento',
    finalizada: 'visitas_finalizadas',
    cancelada: 'visitas_canceladas',
  };
  const optionStr = statusOptionMap[status] ?? 'visitas_agendadas';

  useEffect(() => {
    setSelectedPage({ option: optionStr });
  }, [optionStr]);

  useEffect(() => {
    setLoading(true);
    fetchVisits().finally(() => setLoading(false));
  }, [limit, page, status]);

  if (loading) {
    return (
      <ContentWrapper
        loading={loading}
        row={selectedRow}
        openModal={!!modalMode}
        modalType={modalMode}
        handleCloseModal={() => {
          setModalMode(null);
          setSelectedRow(null);
        }}
        pageOption={optionStr}
      >
        <LoadingData title="Carregando..." />
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper
      loading={loading}
      row={selectedRow}
      openModal={!!modalMode}
      modalType={modalMode}
      handleCloseModal={() => {
        setModalMode(null);
        setSelectedRow(null);
      }}
      modifyViewMode={modifyViewMode}
      setModifyViewMode={setModifyViewMode}
      pageOption={optionStr}
    >
      {rows.length ? (
        <>
          {modifyViewMode.mode === 'normal' ? (
            <Table
              rows={rows}
              setSelectedRow={setSelectedRow}
              setModalType={setModalMode}
            />
          ) : (
            <ListCardsPQFLVisits
              data={rows}
              onVisualize={handleVisualize}
              onPrint={visit => {
                setSelectedRow(visit);
                setShowPrintSelection(true);
                return Promise.resolve();
              }}
            />
          )}
          <Paginator
            size={rows.length}
            limit={limit}
            setLimit={setLimit}
            page={page}
            setPage={setPage}
            hasMore={hasMore}
            count={count}
          />
        </>
      ) : (
        <DontExistsItemFlex>
          <DontExistsItem title="Nenhuma visita encontrada" />
        </DontExistsItemFlex>
      )}
      {showPrintSelection && selectedRow && (
        <ModalInfoTemplete
          isOpen={showPrintSelection}
          closeModal={() => setShowPrintSelection(false)}
          titleModal="Opções de Impressão"
          option="visitas_finalizadas"
          medium
        >
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <p style={{ marginBottom: '20px', fontSize: '1.1em' }}>
              Como deseja gerar o relatório da visita?
            </p>
            <div
              style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}
            >
              <CustomButtonWithIcon
                title="Sem fotos"
                width={180}
                action={() => {
                  setShowPrintSelection(false);
                  handlePrintRequest(selectedRow, false);
                }}
              />
              <CustomButtonWithIcon
                title="Com fotos"
                width={180}
                action={() => {
                  setShowPrintSelection(false);
                  handlePrintRequest(selectedRow, true);
                }}
              />
            </div>
          </div>
        </ModalInfoTemplete>
      )}
    </ContentWrapper>
  );
};
